import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageOff } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SuggestionThumbnailProps {
  /** Best-effort preview resolved at submit time; null for platforms we cannot preview. */
  url: string | null;
  className?: string;
}

/**
 * A suggestion's preview image, or a neutral placeholder.
 *
 * Two reasons this is a component rather than a bare `<img>`: a missing preview
 * must still occupy the same box so rows don't jump, and a preview URL can rot
 * after it was stored — TikTok's oEmbed thumbnails live on a CDN that expires
 * them — which would otherwise render as a broken-image glyph. Both cases
 * collapse to the same placeholder.
 */
export function SuggestionThumbnail({ url, className }: SuggestionThumbnailProps) {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);

  // A row can be replaced by another while this component stays mounted (paging
  // or filtering the admin table), so a previous failure must not stick.
  useEffect(() => { setFailed(false); }, [url]);

  const box = cn('shrink-0 rounded bg-muted object-cover', className);

  if (!url || failed) {
    return (
      <div
        className={cn(box, 'flex items-center justify-center')}
        title={t('templateIdeas.noPreview')}
        data-testid="suggestion-thumbnail-placeholder"
      >
        <ImageOff className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <span className="sr-only">{t('templateIdeas.noPreview')}</span>
      </div>
    );
  }

  return (
    <img
      src={url}
      alt=""
      className={box}
      loading="lazy"
      onError={() => setFailed(true)}
      data-testid="suggestion-thumbnail"
    />
  );
}
